'use client'

import { Radio } from 'react-aria-components'
import { twMerge } from '@/lib/classname/twMerge'
import { cva } from 'class-variance-authority'
import type { PrivateUiInputProps } from '../../_shared/AbstractInput.client'
import { RadioBullet } from './RadioBullet.client'

type RadioButtonOwnProps = {
  id?: string
  className?: string
  description?: React.ReactNode
  renderCustomInput?: (props: {
    value: string
    onChange: (value: string) => void
  }) => React.ReactElement
  'data-testid'?: string
}
type RadioButtonProps = PrivateUiInputProps<string, RadioButtonOwnProps>

const radioItemStyles = cva('flex cursor-pointer flex-col items-start', {
  variants: {
    isDisabled: {
      true: 'cursor-default',
    },
    isFocusVisible: {
      true: 'outline outline-2 outline-offset-2 outline-brand-2-900',
    },
  },
})

const radioLabelStyles = cva('', {
  variants: {
    isDisabled: {
      true: 'text-disabled-fg',
    },
    error: {
      true: 'text-error-fg',
    },
  },
  compoundVariants: [
    {
      isDisabled: true,
      error: true,
      className: 'text-error-fg',
    },
  ],
})

const radioDescriptionStyles = cva('ms-3 text-xs text-grey-500', {
  variants: {
    isDisabled: {
      true: 'text-disabled-fg',
    },
  },
})

export function RadioButton({
  id,
  value,
  className,
  label,
  description,
  isDisabled,
  error,
  onChange,
  'data-testid': dataTestId,
  isSubmitting,
  isLoading,
  renderCustomInput,
}: RadioButtonProps) {
  if (renderCustomInput) {
    return renderCustomInput({ value, onChange })
  }

  const isRadioDisabled = isDisabled || isSubmitting || isLoading

  return (
    <Radio
      id={id}
      data-testid={dataTestId}
      value={value}
      isDisabled={isRadioDisabled}
      className={({ isDisabled: radioDisabled, isFocusVisible }) =>
        twMerge(className, radioItemStyles({ isDisabled: radioDisabled, isFocusVisible }))
      }
    >
      {({ isSelected, isDisabled: radioDisabled }) => (
        <>
          <span className="flex items-center gap-x-1">
            <RadioBullet isSelected={isSelected} isInvalid={!!error} isDisabled={radioDisabled} />
            <span className={radioLabelStyles({ isDisabled: radioDisabled, error: !!error })}>
              {label}
            </span>
          </span>

          {description && (
            <span className={radioDescriptionStyles({ isDisabled: radioDisabled })}>
              {description}
            </span>
          )}
        </>
      )}
    </Radio>
  )
}
